import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface MessageEnvelope {
  code: 'success'
  message?: string
}

export interface UserProfile {
  avatar?: string | null
  created_at?: string | null
  email: string
  email_managed?: boolean
  id: string
  last_active_at?: string | null
  name: string
  role: string
  status: string
  timezone?: string | null
  updated_at?: string | null
}

export interface UserProfileUpdatePayload {
  name?: string
  timezone?: string | null
}

export interface UserPasswordUpdatePayload {
  currentPassword: string
  newPassword: string
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('user'),
  portal: 'user',
})

export async function getUserProfile() {
  const response = await apiClient.request<SuccessEnvelope<UserProfile>>('/app/user')

  return response.data
}

export async function updateUserProfile(values: UserProfileUpdatePayload) {
  const body: UserProfileUpdatePayload = {}

  if (values.name !== undefined) {
    body.name = values.name.trim()
  }
  if (values.timezone !== undefined) {
    const normalizedTimezone = values.timezone?.trim()
    body.timezone = normalizedTimezone === '' ? null : (normalizedTimezone ?? null)
  }

  const response = await apiClient.request<SuccessEnvelope<UserProfile>>('/app/user', {
    body: JSON.stringify(body),
    method: 'PUT',
  })

  return response.data
}

export async function uploadUserAvatar(file: File) {
  const formData = new FormData()
  formData.append('avatar', file)

  const response = await apiClient.request<SuccessEnvelope<{ avatar: string }>>(
    '/app/user/upload-avatar',
    {
      body: formData,
      method: 'POST',
    },
  )

  return response.data.avatar
}

export async function deleteUserAvatar() {
  await apiClient.request<MessageEnvelope>('/app/user/avatar', {
    method: 'DELETE',
  })
}

export async function updateUserPassword(values: UserPasswordUpdatePayload) {
  await apiClient.request<MessageEnvelope>('/app/user/password', {
    body: JSON.stringify({
      current_password: values.currentPassword,
      new_password: values.newPassword,
    }),
    method: 'PUT',
  })
}
